import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { emptyProgramGameForm, PROGRAM_GAME_DEFAULT_GAME_TYPE } from "../constants";
import { defaultOwnershipSelections } from "../formUtils";
import type { OwnershipOption, ProgramGameFormState, ProgramGameSelectOption } from "../types";

interface UseProgramGameFormEffectsParams {
	isOpen: boolean;
	typeOptions: ProgramGameSelectOption[];
	isAdminUser: boolean;
	ownershipItems: OwnershipOption[];
	setForm: Dispatch<SetStateAction<ProgramGameFormState>>;
}

export function useProgramGameFormEffects({
	isOpen,
	typeOptions,
	isAdminUser,
	ownershipItems,
	setForm,
}: UseProgramGameFormEffectsParams) {
	const ownershipDefaultAppliedRef = useRef(false);

	useEffect(() => {
		if (!isOpen) {
			ownershipDefaultAppliedRef.current = false;
			return;
		}
		setForm(emptyProgramGameForm());
	}, [isOpen, setForm]);

	useEffect(() => {
		if (!isOpen || typeOptions.length === 0) {
			return;
		}
		setForm((prev) => {
			if (prev.gameType !== null) {
				return prev;
			}
			const defaultGameType = typeOptions.find((t) => t.value === PROGRAM_GAME_DEFAULT_GAME_TYPE);
			return {
				...prev,
				gameType: defaultGameType ?? typeOptions[0],
			};
		});
	}, [isOpen, typeOptions, setForm]);

	useEffect(() => {
		if (!isOpen || !isAdminUser || ownershipDefaultAppliedRef.current) {
			return;
		}
		const defaultSelections = defaultOwnershipSelections(ownershipItems);
		if (defaultSelections.length === 0) {
			return;
		}
		ownershipDefaultAppliedRef.current = true;
		setForm((prev) => ({ ...prev, ownershipSelections: defaultSelections }));
	}, [isOpen, isAdminUser, ownershipItems, setForm]);

}
